import { useEffect } from 'react';

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: 'website' | 'article';
  jsonLd?: Record<string, unknown>;
}

const SITE_URL: string =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL
    ? import.meta.env.VITE_SITE_URL.replace(/\/+$/, '')
    : 'https://superbuyspreadsheet.bar';

function setMetaTag(attrs: Record<string, string>) {
  const selector = Object.entries(attrs)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join('');
  let el = document.querySelector(`meta${selector}`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    el.setAttribute('content', attrs.content || '');
  }
}

function setLinkTag(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageSEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  jsonLd,
}: PageSEOProps) {
  useEffect(() => {
    document.title = title;

    setMetaTag({ name: 'description', content: description });
    if (keywords) {
      setMetaTag({ name: 'keywords', content: keywords });
    }

    const url = canonicalPath ? `${SITE_URL}${canonicalPath}` : `${SITE_URL}/`;
    setLinkTag('canonical', url);

    setMetaTag({ property: 'og:title', content: title });
    setMetaTag({ property: 'og:description', content: description });
    setMetaTag({ property: 'og:url', content: url });
    setMetaTag({ property: 'og:type', content: ogType });

    setMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
    setMetaTag({ name: 'twitter:title', content: title });
    setMetaTag({ name: 'twitter:description', content: description });

    if (jsonLd) {
      const existing = document.querySelector('script[type="application/ld+json"][data-page-seo]');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-seo', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.querySelector('script[type="application/ld+json"][data-page-seo]');
      if (script) script.remove();
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd]);
}